import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Card, CardContent } from "@/components/ui/card";
import {
  Users, AlertTriangle, ShieldAlert, Layers,
  Database, Building2, Wallet, KeyRound, ArrowRight,
} from "lucide-react";
import { ProvenancePanel } from "@/components/common/ProvenancePanel";

/**
 * Dashboard — DRAFT
 *
 * Renders only the KPIs explicitly requested by the product owner:
 *  1. Total number of Consumers
 *  2. Total number of Consumers At Risk
 *  3. Total number of Consumers identified as Vulnerable
 *  4. Total number of Consumers across the product range (Banking / Lending /
 *     Insurance-Risk / Investments)
 *
 * Below each KPI, the "Operational data provenance" panel documents:
 *  - Source (new)       — how the KPI is fed when a consumer is created/updated
 *  - Source (existing)  — how the KPI is fed from historical data at go-live
 *  - To operationalise  — the connectors/policies the institution must put in place
 *
 * Everything below is traceable to the architectural brief in the original
 * problem statement (CRM / Core Banking / Insurance / Learning / Customer
 * Support / Complaints / Identity / Document / Notification categories).
 */

const PROVENANCE = [
  {
    key: "total_consumers",
    icon: Users,
    label: "Total consumers",
    new: {
      systems: "CRM · Core Banking · Insurance · Investment platforms",
      detail:
        "Emitted as consumer.created.v1 / consumer.updated.v1 events by CRM (Salesforce, Microsoft Dynamics, HubSpot, SAP CRM) and Line-of-Business systems (Temenos, Mambu, Finacle, Oracle FLEXCUBE, Finastra, Backbase, Guidewire, Sapiens, Duck Creek, FINEOS) whenever a consumer is onboarded or their master record changes.",
    },
    existing: {
      systems: "Consumer master file · CRM + Line-of-Business snapshot",
      detail:
        "One-time bulk migration of the institution's existing consumer master records from CRM plus every Line-of-Business platform (Core Banking, Insurance, Investments). Delivered via REST bulk endpoints or SFTP, deduplicated against Identity records from Microsoft Entra / Okta / Keycloak / Auth0.",
    },
    operationalise: [
      "Connect at least one CRM connector (Salesforce · Dynamics · HubSpot · SAP CRM).",
      "Connect at least one Line-of-Business connector (Core Banking or Insurance or Investments) so every consumer is anchored to a real product relationship.",
      "Perform initial bulk load, then subscribe to consumer.created.v1 / consumer.updated.v1 events via the Integration Framework.",
      "Use Identity connectors (Entra / Okta / Keycloak / Auth0) to deduplicate consumers across source systems.",
    ],
  },
  {
    key: "consumers_at_risk",
    icon: AlertTriangle,
    label: "Consumers at risk",
    new: {
      systems: "Complaints · Customer Support · Learning · Assessments",
      detail:
        "Trust band = At risk is a deterministic composite driven by: complaint counts and status (Salesforce Service Cloud, Dynamics Customer Service, Zendesk, Custom REST APIs), support interaction volume (Genesys, Amazon Connect, Zendesk, Freshdesk, Microsoft Dynamics), assessment scores and literacy bands (Altara Consumer Education, Moodle, Cornerstone, SAP SuccessFactors, Docebo, SCORM, xAPI). Rules R-LIT-001/002, R-CMP-001 and R-VUL-001 combine these signals into the At risk band.",
    },
    existing: {
      systems: "12–24 month event backfill",
      detail:
        "Historical backfill of complaint.logged.v1, complaint.resolved.v1, support.interaction.v1, assessment.completed.v1 and learning.completed.v1 events for the trailing 12–24 months from Complaints, Customer Support and Learning systems. Loaded via bulk file drops or paginated REST reads, then normalised through the Integration Framework.",
    },
    operationalise: [
      "Connect Complaints (Salesforce Service Cloud · Dynamics Customer Service · Zendesk · Custom REST APIs).",
      "Connect Customer Support (Genesys · Amazon Connect · Zendesk · Freshdesk · Microsoft Dynamics).",
      "Connect at least one Assessment provider (Altara Consumer Education · Moodle · Cornerstone · SAP SuccessFactors · Docebo · SCORM · xAPI).",
      "Publish complaint / support / assessment events to the Altara Event Bus so deterministic rules can re-evaluate in real time.",
    ],
  },
  {
    key: "consumers_vulnerable",
    icon: ShieldAlert,
    label: "Consumers identified as vulnerable",
    new: {
      systems: "CRM · Complaints · Vulnerability Register API",
      detail:
        "Vulnerability indicators (financial hardship, chronic illness, low digital literacy, language barrier, recent job loss, etc.) are recorded through explicit institutional channels only. Sources include: (a) consumer self-declaration captured during onboarding via CRM (Salesforce / Dynamics / HubSpot / SAP CRM); (b) advisor or Conduct Risk officer flagging on a case in Complaints (Salesforce Service Cloud / Dynamics Customer Service); (c) automated flags from Core Banking based on defined regulatory triggers; (d) dedicated Vulnerability Register REST API for policy-driven updates.",
    },
    existing: {
      systems: "Vulnerability Register migration",
      detail:
        "One-time migration of the institution's existing Vulnerability Register — commonly held as CRM custom fields, SharePoint / OpenText / Alfresco / Box documents, or standalone spreadsheets. Imported via file drop or REST, then reconciled against the Consumer master via Identity connectors.",
    },
    operationalise: [
      "Institution must maintain a documented Vulnerability Policy aligned to the relevant regulator (FSCA · FCA · Consumer Duty · IOSCO fair-outcome principles).",
      "A Vulnerability Register must be owned by the Conduct Risk or Compliance function.",
      "Connect either a CRM / Complaints integration surfacing vulnerability tags, or Altara's dedicated Vulnerability API.",
      "Consent, lawful-basis, and data-protection controls (POPIA · GDPR) must be in place before capture; Document Management connectors (SharePoint · OpenText · Alfresco · Box) hold the evidentiary artefacts.",
    ],
  },
  {
    key: "product_range",
    icon: Layers,
    label: "Consumers across product range",
    new: {
      systems: "Core Banking · Insurance · Investment platforms",
      detail:
        "product.opened.v1, product.closed.v1 and product.status.changed.v1 events emitted by Core Banking (Temenos · Mambu · Finacle · Oracle FLEXCUBE · Finastra · Backbase) for Banking + Lending, Insurance systems (Guidewire · Sapiens · Duck Creek · FINEOS) for Insurance / Risk, and Investment platforms (via Custom REST connectors) for Investments. Each event is mapped to Altara's canonical product taxonomy at ingestion.",
    },
    existing: {
      systems: "Product master snapshot per Line-of-Business",
      detail:
        "One-time snapshot of accounts, cards, loans, mortgages, policies and investment holdings from each Line-of-Business platform. Delivered via bulk REST or SFTP with periodic reconciliation. Consumer-to-product linkage is anchored on the deduplicated Consumer ID established during the consumer master load.",
    },
    operationalise: [
      "Connect Core Banking (Temenos · Mambu · Finacle · Oracle FLEXCUBE · Finastra · Backbase) for Banking + Lending groups.",
      "Connect Insurance systems (Guidewire · Sapiens · Duck Creek · FINEOS) for Insurance / Risk group.",
      "Connect Investment platforms via Custom REST connector for the Investments group.",
      "Map every source product code to Altara's canonical taxonomy (current_account · savings · credit_card · loan · mortgage · insurance · investment) through the Integration Framework mapper.",
    ],
  },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/summary").then(r => setData(r.data)); }, []);

  return (
    <div data-testid={TID.dashboard} className="space-y-6 animate-fade-in-up">
      <header className="flex items-end justify-between border-b border-border pb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">Executive dashboard</span>
            <span className="text-[10px] uppercase tracking-[0.18em] font-medium bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-sm">
              DRAFT · v0
            </span>
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-altara-ink mt-1">
            {data?.tenant_name || "Loading…"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Foundational KPIs only. Additional tiles will be added on request.
          </p>
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono">
          Phase 1 · Consumer Intelligence
        </div>
      </header>

      {/* Platform architecture strip — teaches BRD at a glance */}
      <PlatformArchitectureStrip />

      {/* KPI 1 · Total Consumers ─────────────────────────────── */}
      {/* KPI 2 · Consumers At Risk ───────────────────────────── */}
      {/* KPI 3 · Consumers Identified as Vulnerable ──────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiTile
          testid={TID.kpiTotalConsumers}
          label="Total consumers"
          icon={Users}
          value={data?.total_consumers}
          tone="neutral"
          hint="Living Consumer Intelligence Profiles under this tenant"
        />
        <KpiTile
          testid="kpi-consumers-at-risk"
          label="Consumers at risk"
          icon={AlertTriangle}
          value={data?.consumers_at_risk}
          tone="rose"
          hint="Trust band = At risk (deterministic composite)"
        />
        <KpiTile
          testid="kpi-consumers-vulnerable"
          label="Consumers identified as vulnerable"
          icon={ShieldAlert}
          value={data?.consumers_vulnerable}
          tone="amber"
          hint="One or more vulnerability indicators recorded on the profile"
        />
      </div>

      {/* KPI 4 · Consumers Across Product Range ──────────────── */}
      <Card className="rounded-sm border-slate-200 shadow-none">
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers className="w-3.5 h-3.5 text-altara-teal" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Consumers across product range</div>
                <div className="text-sm font-medium text-altara-ink">Distinct consumers holding at least one product in each group</div>
              </div>
            </div>
            <div className="text-[10px] font-mono text-muted-foreground">Draft groupings</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-100">
            {(data?.product_breakdown || []).map((g) => (
              <div
                key={g.group}
                data-testid={`kpi-product-${g.group.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="p-4"
              >
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{g.group}</div>
                <div className="font-display text-3xl font-semibold text-altara-ink tabular-nums mt-1">
                  {g.consumers}
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">consumers</div>
              </div>
            ))}
            {!data && (
              <div className="col-span-full p-6 text-center text-sm text-muted-foreground">Loading…</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Operational data provenance ───────────────────────── */}
      <ProvenancePanel
        entries={PROVENANCE}
        title="Operational data provenance"
        subtitle="Where each KPI is drawn from for new and existing consumers, and what institutions must connect to operationalise."
        testId="dashboard-data-provenance"
      />

      {/* Draft note */}
      <div className="border border-dashed border-slate-300 rounded-sm p-4 text-xs text-slate-500 bg-slate-50/60">
        <div className="font-semibold text-slate-700 mb-1">Dashboard is in draft.</div>
        Additional tiles (recommendations, evidence, trust trend, category breakdown, activity feed, benchmark
        comparisons) will be added on request. Nothing else has been assumed.
      </div>
    </div>
  );
}

function KpiTile({ testid, label, icon: Icon, value, hint, tone = "neutral" }) {
  const toneMap = {
    neutral: { bg: "bg-white", ring: "border-slate-200", icon: "text-altara-teal", number: "text-altara-ink" },
    rose: { bg: "bg-rose-50/40", ring: "border-rose-200", icon: "text-rose-700", number: "text-rose-800" },
    amber: { bg: "bg-amber-50/40", ring: "border-amber-200", icon: "text-amber-700", number: "text-amber-800" },
  };
  const t = toneMap[tone] || toneMap.neutral;
  return (
    <Card data-testid={testid} className={`rounded-sm shadow-none border ${t.ring} ${t.bg}`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
          <Icon className={`w-4 h-4 ${t.icon}`} strokeWidth={2} />
        </div>
        <div className={`mt-3 font-display text-5xl font-semibold tabular-nums leading-none ${t.number}`}>
          {value === null || value === undefined ? "…" : value}
        </div>
        <div className="text-[11px] text-slate-500 mt-3 leading-relaxed">{hint}</div>
      </CardContent>
    </Card>
  );
}

function PlatformArchitectureStrip() {
  const NAVY = "#0A1F44";
  const GOLD = "#C9A24A";
  const TEAL = "#0F766E";
  const cards = [
    {
      icon: Database, iconTone: TEAL, iconBg: "#CCFBF1",
      eyebrow: "The Data Lake",
      title: "Altara Consumer Intelligence Profile",
      body: "One canonical row per consumer. Personal Data Vault, consent records, evidence, journeys — all here. Tenant-isolated. Read-only for admin (audited); nothing goes to institutions without consent.",
      cta: { label: "Consumer Register", to: "/consumers" },
    },
    {
      icon: Building2, iconTone: NAVY, iconBg: "#E2E8F0",
      eyebrow: "Institution Portal",
      title: "Tenant-scoped, zero cross-contamination",
      body: "Each bank / insurer / gaming operator is a Tenant with its own org ID and API keys. Requests consent, pulls only granted data, sees only its own consumer aggregates. Consent Ledger is the only bridge to the lake.",
      cta: { label: "Institutions", to: "/institutions" },
    },
    {
      icon: KeyRound, iconTone: GOLD, iconBg: "#FEF3C7",
      eyebrow: "The Consent Bridge",
      title: "Every access is purpose-bound & revocable",
      body: "Institutions POST /api/consent/requests → consumer approves in the wallet → institution pulls scoped data. Every read hash-chained + logged. Revoke stops future reads immediately.",
      cta: { label: "Consent Ledger", to: "/consent-ledger" },
    },
    {
      icon: Wallet, iconTone: NAVY, iconBg: "#FEF3C71A",
      eyebrow: "Consumer Wallet",
      title: "PWA · consumer controls their data",
      body: "Consumer enters identity ONCE. Sees every institution asking, grants/declines/revokes. Sees every read that ever happened. Installable on Android / iOS.",
      cta: { label: "Open Wallet →", to: "/wallet" },
    },
  ];
  return (
    <div data-testid="platform-architecture-strip" className="rounded-sm border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>
          Platform architecture
        </div>
        <div className="text-[10px] text-slate-500">
          How Altara, institutions, and consumers connect.
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <Link
            key={c.title}
            to={c.cta.to}
            className="rounded-sm border border-slate-100 p-4 hover:border-slate-300 hover:shadow-sm transition group flex flex-col"
            data-testid={`arch-card-${c.eyebrow.toLowerCase().replace(/\s/g, "-")}`}
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ background: c.iconBg }}>
                <c.icon className="w-4 h-4" style={{ color: c.iconTone }} />
              </div>
              <div className="text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: c.iconTone }}>
                {c.eyebrow}
              </div>
            </div>
            <div className="text-[13px] font-semibold mt-2.5" style={{ color: NAVY }}>{c.title}</div>
            <div className="text-[11px] text-slate-600 leading-snug mt-1.5 flex-1">{c.body}</div>
            <div className="mt-3 text-[11px] font-semibold flex items-center gap-1 group-hover:gap-2 transition-all" style={{ color: NAVY }}>
              {c.cta.label} <ArrowRight className="w-3 h-3" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
